import { ChevronDownIcon, ChevronRightIcon, LockIcon, SlidersHorizontalIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatches } from 'react-router-dom';

import { classnames } from '@nicoknoll/utils';
import { useQuery } from '@tanstack/react-query';

import CookieBar from '../../../components/CookieBar.tsx';
import Footer from '../../../components/Footer.tsx';
import Header from '../../../components/Header.tsx';
import Tooltip from '../../../components/Tooltip.tsx';
import { useCustomization } from '../../../customizations/utils.tsx';
import { eventDetailQuery } from '../../../data/queries.ts';
import {
    SITE_EVENT_DETAIL_PATH,
    SITE_JOB_LIST_PATH,
    SITE_ORGANIZATION_LIST_PATH,
    SITE_SESSIONS_LIST_PATH,
    SITE_USER_LIST_PATH,
} from '../paths.ts';
import { siteUrl, useEventId } from '../utils.ts';
import Navigation from './Navigation.tsx';

export const SiteLayout = () => {
    const matches = useMatches();
    const lastMatch = matches[matches.length - 1];

    const { hideNavigation = false, hideFooter = false } = lastMatch?.handle || ({} as any);

    return (
        <div className="antialiased min-h-[100vh] flex flex-col">
            <Header />

            {!hideNavigation && <SiteNavigation />}

            {/*<div className="min-h-[100vh] bg-white">*/}
            <Outlet />
            {/*</div>*/}

            {!hideFooter && <Footer />}

            <CookieBar />
        </div>
    );
};

export const SiteNavigation = (props: React.ComponentPropsWithRef<typeof Navigation>) => {
    const { t } = useTranslation('site');

    const eventId = useEventId();
    const { data: event } = useQuery(eventDetailQuery(eventId!));

    const isUserProfilesEnabled = useCustomization('isUserProfilesEnabled', true);
    const isJobsEnabled = useCustomization('isJobsEnabled', true);

    return (
        <Navigation {...props}>
            <Navigation.Link to={siteUrl(SITE_EVENT_DETAIL_PATH, { eventId })} end>
                {t('siteNavigation.home')}
            </Navigation.Link>

            {event?.hasSessions ? (
                <Navigation.Link to={siteUrl(SITE_SESSIONS_LIST_PATH, { eventId })}>
                    {t('siteNavigation.sessions')}
                </Navigation.Link>
            ) : null}

            {event?.hasOrganizationProfiles ? (
                <Navigation.Link to={siteUrl(SITE_ORGANIZATION_LIST_PATH, { eventId })}>
                    {t('siteNavigation.organizations')}
                </Navigation.Link>
            ) : null}

            {isUserProfilesEnabled && (
                <Tooltip>
                    <Tooltip.Trigger asChild>
                        <Navigation.Link to={siteUrl(SITE_USER_LIST_PATH, { eventId })}>
                            <span className="flex gap-2 items-center">
                                <LockIcon />
                                <span>{t('siteNavigation.users')}</span>
                            </span>
                        </Navigation.Link>
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                        <span className="flex gap-1.5 items-center">
                            <LockIcon className="!w-3 !h-3" strokeWidth={2.5} />
                            <span>{t('siteNavigation.usersTooltip')}</span>
                        </span>
                    </Tooltip.Content>
                </Tooltip>
            )}

            {isJobsEnabled && event?.hasJobs && (
                <Navigation.Link to={siteUrl(SITE_JOB_LIST_PATH, { eventId })}>
                    {t('siteNavigation.jobs')}
                </Navigation.Link>
            )}
        </Navigation>
    );
};

export const SiteFilters = ({ className, children, ...props }: React.ComponentPropsWithRef<'div'>) => {
    const { t } = useTranslation('site');

    const [collapsed, setCollapsed] = useState(true);
    return (
        <div
            className={classnames(
                'flex flex-col w-80 flex-none border-r border-base-150 pr-9 overflow-hidden max-sm:w-full max-sm:border max-sm:rounded-2xl max-sm:pr-0',
                className
            )}
            {...props}
        >
            <button
                className="hidden max-sm:flex w-full justify-between items-center px-8 py-6 hover:bg-base-50 transition-colors"
                onClick={() => setCollapsed(!collapsed)}
            >
                <span className="flex gap-3 items-center">
                    <SlidersHorizontalIcon />
                    <span className="font-medium text-lg">{t('siteFilters.title')}</span>
                </span>
                {collapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </button>
            <div className={classnames('max-sm:border-t max-sm:p-8 border-base-150', collapsed && 'max-sm:hidden')}>
                {children}
            </div>
        </div>
    );
};

export const SiteMain = ({ className, ...props }: React.ComponentPropsWithRef<'main'>) => {
    return (
        <main className={classnames('flex justify-center bg-white px-6 py-24 max-sm:py-16', className)} {...props} />
    );
};

export const SiteContentWithInfoBoxes = ({ className, children, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div className="w-full max-w-[70rem] flex flex-col gap-32">
            <section className="flex gap-10 max-sm:flex-col max-sm:gap-16">{children}</section>
        </div>
    );
};

export const SiteInfoBoxes = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return <div className={classnames('flex flex-col gap-12 w-4/12 max-sm:w-full', className)} {...props} />;
};
